import React from 'react';
import PropTypes from 'prop-types';
import CloseIcon from 'mdi-react/CloseIcon';
import StarIcon from 'mdi-react/StarIcon';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';

// The button a slide gets (as in the app's storyUtils.slideAction): only
// tiles that point to something get one, and videos play in place
export const slideAction = (contentType, contentId) => {
  if (!contentId) return null;
  if (contentType === 'CUSTOM_VIDEO' && /^https?:\/\//.test(`${contentId}`)) {
    return null;
  }
  switch (contentType) {
    case 'YOUTUBE_VIDEO':
      return 'storyWatch';
    case 'EXTERNAL_LINK':
    case 'WEBVIEW_LINK':
      return 'storyOpenLink';
    default:
      return 'storyReadMore';
  }
};

// Readable text on the accent colour (light or dark)
const textOn = (hex) => {
  const match = /^#?([0-9a-f]{6})/i.exec(hex || '');
  if (!match) return '#121B2B';
  const num = parseInt(match[1], 16);
  const [r, g, b] = [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  return r * 0.299 + g * 0.587 + b * 0.114 > 160 ? '#121B2B' : '#FFFFFF';
};

/**
 * A story slide as the app shows it full screen (StoryViewer): the image,
 * progress bars and the group title on top, then the big figure, title,
 * text and button at the bottom.
 */
const SpotlightStoryPreview = ({
  group,
  tile,
  compact,
  index,
  count,
  exclusive,
  locked,
}) => {
  const p = partial('HighlightBannerForm');
  const config = useConfig();
  const image = tile?.headingImage?.croppedImage || tile?.headingImage?.preview;
  const accent = group?.accentColor || '#FFFFFF';
  const contentId = tile?.contentType?.value ?? tile?.contentType;
  const slideButton = slideAction(tile?.content?.value, contentId);
  // Locked: the button only says how to get access (storyUtils.slideAction)
  const action = slideButton && locked ? 'storyUnlock' : slideButton;
  return (
    <div className={`story-preview${compact ? ' story-preview--compact' : ''}`}>
      <div
        className={`story-preview__screen${
          locked ? ' story-preview__screen--locked' : ''
        }`}
        style={image ? { backgroundImage: `url(${image})` } : undefined}
      >
        {!image && !locked && (
          <span className="story-preview__noimage">
            {p('storyPreviewNoImage')}
          </span>
        )}
        {/* No access: the slide blurred and dimmed, with the club's logo and
            the explanation, as a locked video in shorts */}
        {locked && (
          <div className="story-preview__locked">
            {image && (
              <span
                className="story-preview__locked-blur"
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url(${image})`,
                }}
              />
            )}
            <div className="story-preview__locked-content">
              {config.club_logo && <img src={config.club_logo} alt="" />}
              <strong>{p('storyExclusive')}</strong>
              <span>{p('storyLockedTitle')}</span>
              {!compact && <span>{p('storyLockedText')}</span>}
            </div>
          </div>
        )}
        <div className="story-preview__top">
          {/* This slide's bar part-way; the ones before it full */}
          <div className="story-preview__bars">
            {Array.from({ length: Math.max(count, 1) }, (_, bar) => (
              <span key={bar}>
                {bar <= index && (
                  <span
                    style={{
                      background: accent,
                      width: bar < index ? '100%' : '45%',
                    }}
                  />
                )}
              </span>
            ))}
          </div>
          <div className="story-preview__header">
            <span>{group?.title || ''}</span>
            <CloseIcon size={18} />
          </div>
          {/* Members-only, under the close button */}
          {exclusive && !locked && (
            <span className="story-preview__exclusive">
              <StarIcon size={10} />
              {p('storyExclusive')}
            </span>
          )}
        </div>
        <div className="story-preview__bottom">
          {!locked && !!tile?.highlight && (
            <span className="story-preview__highlight">{tile.highlight}</span>
          )}
          {!locked && !!tile?.title && (
            <span className="story-preview__title">{tile.title}</span>
          )}
          {!locked && !!tile?.description && (
            <span className="story-preview__description">
              {tile.description}
            </span>
          )}
          {action && (
            <span
              className="story-preview__action"
              style={{ background: accent, color: textOn(accent) }}
            >
              {p(action)}
            </span>
          )}
        </div>
      </div>
      {!compact && (
        <p className="story-preview__caption">{p('storyPreviewCaption')}</p>
      )}
    </div>
  );
};

SpotlightStoryPreview.propTypes = {
  group: PropTypes.shape({
    title: PropTypes.string,
    accentColor: PropTypes.string,
  }),
  tile: PropTypes.shape({}),
  compact: PropTypes.bool,
  // Which slide this is, and how many the story has (for the bars)
  index: PropTypes.number,
  count: PropTypes.number,
  // Members-only; locked = as someone without access sees it
  exclusive: PropTypes.bool,
  locked: PropTypes.bool,
};

SpotlightStoryPreview.defaultProps = {
  group: null,
  tile: null,
  compact: false,
  index: 0,
  count: 3,
  exclusive: false,
  locked: false,
};

/**
 * The story on the home screen, as the app's StoriesRow: story groups next
 * to each other in the layout share one row under a "Stories" header, each
 * a round bubble with the first slide's image in a ring of the accent
 * colour, and the group's title under it. Faded bubbles stand in for any
 * neighbouring story groups.
 */
export const SpotlightStoriesRow = ({ group, tiles, exclusive }) => {
  const p = partial('HighlightBannerForm');
  const first = tiles[0]?.tile;
  const image =
    first?.headingImage?.croppedImage || first?.headingImage?.preview;
  return (
    <div className="stories-row">
      <span className="stories-row__header">{p('storiesRow')}</span>
      <div className="stories-row__bubbles">
        <div className="stories-row__bubble">
          <span
            className="stories-row__ring"
            style={{ background: group?.accentColor || '#0469dc' }}
          >
            <span
              className="stories-row__image"
              style={image ? { backgroundImage: `url(${image})` } : undefined}
            />
            {exclusive && (
              <span className="stories-row__exclusive">
                <StarIcon size={10} />
              </span>
            )}
          </span>
          <span className="stories-row__label">{group?.title || ''}</span>
        </div>
        {[1, 2].map((ghost) => (
          <div
            key={ghost}
            className="stories-row__bubble stories-row__bubble--ghost"
            aria-hidden
          >
            <span className="stories-row__ring">
              <span className="stories-row__image" />
            </span>
            <span className="stories-row__label" />
          </div>
        ))}
      </div>
      <p className="stories-row__hint">{p('storiesRowHint')}</p>
    </div>
  );
};

SpotlightStoriesRow.propTypes = {
  group: PropTypes.shape({
    title: PropTypes.string,
    accentColor: PropTypes.string,
  }),
  tiles: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string })),
  exclusive: PropTypes.bool,
};

SpotlightStoriesRow.defaultProps = {
  group: null,
  tiles: [],
  exclusive: false,
};

export default SpotlightStoryPreview;
